// Multi-match sidecar — Phase 1 of the multi-match / friends / turn-based
// roadmap. Holds parked snapshots for every in-flight match the player
// has going, plus a small metadata blob the /matches hub renders without
// having to deserialize the full snapshot.
//
// The CANONICAL active-match data still lives in `useMatchStore`. This
// store sits alongside as a key/value warehouse: when the player leaves
// a match we PARK the active match's state here; when they enter a
// different match URL we LOAD its snapshot back into useMatchStore. So
// existing match logic is untouched — only the navigation seams change.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { HeroId } from '../game/data/heroes';
import { useMatchStore, type MatchState } from './match';

/**
 * Cap on concurrent in-flight matches per player. Beta plan called for
 * 5 — enough to absorb async wait time without making the hub feel like
 * an inbox. Completed/forfeit matches don't count.
 */
export const MATCHES_CAP = 5;

/**
 * Sidecar snapshot — a copy of every field useMatchStore persists. We
 * just reuse MatchState; the field set must stay in sync with what
 * useMatchStore.partialize returns (drift would mean restored matches
 * lose state). Both are owned by the same author so the coupling is
 * acceptable; if it ever diverges, the right move is to extract a
 * shared schema and have both consume it.
 */
export type MatchSnapshot = Omit<MatchState, 'turnTimerActive' | 'heroPowerState' | 'heroPowerTargetType' | 'heroPowerDiscoverOptions' | 'asyncOpponent' | 'lastPublishedSnapshotId' | 'combatRemoteSimSucceeded'> & {
  // Transient fields above are reset on load — explicitly omitted so
  // restored matches always come up clean (no half-open hero-power UI).
};

export type MatchStatus = 'in-progress' | 'completed' | 'forfeit';

export interface MatchMeta {
  matchId: string;
  heroId: HeroId | null;
  status: MatchStatus;
  roundNumber: number;
  health: number;
  placement: number | null;
  /**
   * Handle of the last real human opponent we faced in this match (if
   * any). The hub uses it as the headline ("vs Lupita"); falls back to
   * "AI lobby" when the round was filled by ghosts/AI.
   */
  lastAsyncOpponentHandle: string | null;
  createdAt: number;
  updatedAt: number;
}

interface MatchesState {
  matches: Record<string, MatchSnapshot>;
  meta: Record<string, MatchMeta>;
  /**
   * Tracks which match the singleton useMatchStore is currently holding.
   * Updated by the route-sync hook in RouteLayout; not consumed for
   * persistence (zustand-persist of useMatchStore already covers the
   * active-match data).
   */
  activeMatchId: string | null;
}

interface MatchesActions {
  /** Snapshot the given match payload into the sidecar; bumps updatedAt. */
  park: (matchId: string, snapshot: MatchSnapshot, meta: Omit<MatchMeta, 'createdAt' | 'updatedAt'>) => void;
  /** Remove a match from the sidecar. Clears activeMatchId if it was the parked one. */
  remove: (matchId: string) => void;
  /** Mark which match is currently live in useMatchStore. */
  setActive: (matchId: string | null) => void;
  /** Count of matches still playable (excludes completed/forfeit). */
  countOpen: () => number;
  /** True if creating a new match would exceed MATCHES_CAP. */
  isCapReached: () => boolean;
  /** Repair meta rows the results screen stranded mid-run. See the impl. */
  reconcileFinished: () => void;
  /** Replace the entire meta entry without touching the snapshot — used
   *  when only metadata changes (e.g. async opponent handle resolves). */
  updateMeta: (matchId: string, patch: Partial<Omit<MatchMeta, 'matchId' | 'createdAt'>>) => void;
}

export const useMatchesStore = create<MatchesState & MatchesActions>()(
  persist(
    (set, get) => ({
      matches: {},
      meta: {},
      activeMatchId: null,

      park: (matchId, snapshot, meta) => {
        const now = Date.now();
        set((s) => {
          const existingMeta = s.meta[matchId];
          // Preserve the most recently captured opponent handle. The
          // asyncOpponent slot on MatchState clears at round commit,
          // so naive deriveMeta would forget it between rounds. Sticky
          // last-handle gives the hub something durable to show.
          const stickyOpponent =
            meta.lastAsyncOpponentHandle ?? existingMeta?.lastAsyncOpponentHandle ?? null;
          return {
            matches: { ...s.matches, [matchId]: snapshot },
            meta: {
              ...s.meta,
              [matchId]: {
                ...meta,
                matchId,
                lastAsyncOpponentHandle: stickyOpponent,
                createdAt: existingMeta?.createdAt ?? now,
                updatedAt: now,
              },
            },
          };
        });
      },

      remove: (matchId) => {
        set((s) => {
          const matches = { ...s.matches };
          const meta = { ...s.meta };
          delete matches[matchId];
          delete meta[matchId];
          return {
            matches,
            meta,
            activeMatchId: s.activeMatchId === matchId ? null : s.activeMatchId,
          };
        });
      },

      setActive: (matchId) => set({ activeMatchId: matchId }),

      countOpen: () =>
        Object.values(get().meta).filter((m) => m.status === 'in-progress').length,

      isCapReached: () => get().countOpen() >= MATCHES_CAP,

      // A finished match used to keep its mid-run meta forever, because the
      // only code that refreshes meta is parkCurrent() and the way out of the
      // results screen clears matchId BEFORE navigating:
      //
      //   ResultsPage  endMatch()          -> matchId := null
      //                navigate('/menu')
      //   RouteLayout  parkCurrent()       -> `if (!m.matchId) return;`  no-op
      //
      // so the row stayed status:'in-progress' and kept consuming one of the
      // MATCHES_CAP slots. Five finished runs and you could never start a new
      // game — and finishing one could not free a slot, because finishing was
      // the broken path.
      //
      // ResultsPage now parks before it clears. This repairs the rows already
      // written to real players' localStorage by the shipped build, which that
      // fix cannot reach. The snapshot is the source of truth: it always held
      // the placement, only the meta lost it.
      reconcileFinished: () => {
        const { matches, meta } = get();
        let changed = false;
        const next = { ...meta };
        for (const [matchId, row] of Object.entries(meta)) {
          const placement = matches[matchId]?.placement ?? null;
          if (row.status === 'in-progress' && placement !== null) {
            next[matchId] = { ...row, status: 'completed', placement };
            changed = true;
          }
        }
        if (changed) set({ meta: next });
      },

      updateMeta: (matchId, patch) => {
        set((s) => {
          const existing = s.meta[matchId];
          if (!existing) return s;
          return {
            meta: {
              ...s.meta,
              [matchId]: { ...existing, ...patch, updatedAt: Date.now() },
            },
          };
        });
      },
    }),
    { name: 'cve.v2.matches', version: 1 },
  ),
);

// ---------------------------------------------------------------------------
// Snapshot / meta helpers — pull from useMatchStore in a single place so
// the field list stays consistent.
// ---------------------------------------------------------------------------

/** Build a MatchSnapshot from the current useMatchStore active match. */
export function takeSnapshot(state: MatchState): MatchSnapshot {
  return {
    matchId: state.matchId,
    heroId: state.heroId,
    phase: state.phase,
    booster: state.booster,
    picks: state.picks,
    seed: state.seed,
    heroOptions: state.heroOptions,
    heroRerollsRemaining: state.heroRerollsRemaining,
    roundNumber: state.roundNumber,
    health: state.health,
    gold: state.gold,
    tavernTier: state.tavernTier,
    turnsAtCurrentTier: state.turnsAtCurrentTier,
    hand: state.hand,
    board: state.board,
    shop: state.shop,
    shopFrozen: state.shopFrozen,
    refreshCount: state.refreshCount,
    freeRefreshCredits: state.freeRefreshCredits,
    buysThisTurn: state.buysThisTurn,
    playsThisTurn: state.playsThisTurn,
    opponentBoard: state.opponentBoard,
    lastCombatResult: state.lastCombatResult,
    combatResolved: state.combatResolved,
    opponents: state.opponents,
    currentOpponentIdx: state.currentOpponentIdx,
    pairings: state.pairings,
    roundResults: state.roundResults,
    placement: state.placement,
    pendingTripleReward: state.pendingTripleReward,
    discoverOptions: state.discoverOptions,
    pendingAbilityDiscover: state.pendingAbilityDiscover,
    abilityDiscoverOptions: state.abilityDiscoverOptions,
    turnTimerMs: state.turnTimerMs,
    heroPowerUsedThisTurn: state.heroPowerUsedThisTurn,
  };
}

/** Derive metadata from a match state — used at park time. */
export function deriveMeta(state: MatchState): Omit<MatchMeta, 'createdAt' | 'updatedAt'> {
  // Status — placement set means the match has ended (either victory at
  // placement=1 or elimination at 2..8). Health<=0 is also terminal but
  // placement is the canonical signal.
  const status: MatchStatus = state.placement !== null ? 'completed' : 'in-progress';
  const opp = state.asyncOpponent;
  return {
    matchId: state.matchId ?? '',
    heroId: state.heroId,
    status,
    roundNumber: state.roundNumber,
    health: state.health,
    placement: state.placement,
    lastAsyncOpponentHandle: opp?.handle ?? null,
  };
}

/**
 * Park the currently-active useMatchStore state into the sidecar. No-op
 * if useMatchStore has no matchId (e.g. fresh boot, no match started).
 */
export function parkCurrent(): void {
  const m = useMatchStore.getState();
  if (!m.matchId) return;
  const snapshot = takeSnapshot(m);
  const meta = deriveMeta(m);
  useMatchesStore.getState().park(m.matchId, snapshot, meta);
}

/**
 * Load a previously-parked match into useMatchStore. Returns true if a
 * snapshot was found and loaded, false if the matchId is unknown (the
 * caller can then redirect / show an error).
 *
 * Transient hero-power and timer fields are intentionally reset so the
 * resumed match opens cleanly — same contract as zustand-persist's
 * onRehydrateStorage.
 */
export function loadIntoActive(matchId: string): boolean {
  const snapshot = useMatchesStore.getState().matches[matchId];
  if (!snapshot) return false;
  useMatchStore.setState({
    ...snapshot,
    turnTimerActive: false,
    heroPowerState: 'idle',
    heroPowerTargetType: null,
    heroPowerDiscoverOptions: [],
    asyncOpponent: null,
    lastPublishedSnapshotId: null,
    combatRemoteSimSucceeded: false,
  });
  useMatchesStore.getState().setActive(matchId);
  return true;
}

/**
 * One-time migration — copy the legacy single-match into the sidecar on
 * first boot after deploy. Safe to call on every boot: it's a no-op
 * after the first run.
 */
export function migrateLegacySingleMatch(): void {
  const m = useMatchStore.getState();
  const sidecar = useMatchesStore.getState();
  if (!m.matchId) return;
  if (sidecar.matches[m.matchId]) return; // already migrated
  sidecar.park(m.matchId, takeSnapshot(m), deriveMeta(m));
  sidecar.setActive(m.matchId);
}
